import { useMemo } from 'react'
import { useAppSelector } from "~/store/hooks";
import { SYSTEMS } from 'store/units/constants'
import { Temperature } from 'store/units/types'
import { ImperialTemperature } from 'store/units/types/temperature'
import { FormattedUnitData, UnformattedUnitData } from '~/store/units/types/unit';

const TEMPERATURE_CONVERSION_FACTOR = 1.8

export interface TemperatureFormatter {
  formatTemperature(imperialTemperature: ImperialTemperature<FormattedUnitData | UnformattedUnitData> | number, options?: { round?: number }): string | null
  formatTemperatureDelta(delta: Temperature<UnformattedUnitData>, options?: { round?: number }): string | null
}

export const useFormatTemperature = (): TemperatureFormatter => {
  const { TEMPERATURE } = useAppSelector(({ units }) => units)
  return useMemo(() => {
    const isMetric = TEMPERATURE === SYSTEMS.METRIC
    const formatter = isMetric ? 'formatMetric' : 'formatImperial'
    return {
      formatTemperature: (imperialTemperature, options = { round: 2 }) => {
        return (imperialTemperature instanceof Temperature
          ? imperialTemperature
          : new ImperialTemperature(imperialTemperature))[formatter](options)
      },
      formatTemperatureDelta: (delta, options = { round: 2 }) => {
        if (delta.system !== TEMPERATURE) {
          const { value } = delta
          const convertedValue =
            delta.system === SYSTEMS.METRIC
              ? value! * TEMPERATURE_CONVERSION_FACTOR
              : value! / TEMPERATURE_CONVERSION_FACTOR
          const convertedDelta = new Temperature({ value: convertedValue, system: TEMPERATURE })
          return convertedDelta[formatter](options)
        }
        return delta[formatter](options)
      },
    }
  }, [TEMPERATURE])
}
